import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { CheckCircle, FileText, Shield, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Terms and Conditions Component
export const TermsAndConditions = ({ inModal = false }) => {
  const { t, i18n } = useTranslation();
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTerms();
  }, [i18n.language]); // Refetch when language changes

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/legal/terms?lang=${i18n.language}`);
      setTerms(response.data);
    } catch (error) {
      console.error('Failed to fetch terms:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">{t('loading')}</div>;
  }

  if (!terms) {
    return <div className="text-center p-8 text-red-500">{t('failedToLoad')} {t('termsConditions')}</div>;
  }

  // Check if full_text exists (new format) or use content sections (old format)
  const hasFullText = terms.full_text && terms.full_text.length > 0;

  const content = (
    <div className="space-y-6">
      <div className="text-center border-b pb-4">
        <h1 className="text-2xl font-bold text-gray-900">{terms.title}</h1>
        <div className="flex justify-center gap-4 mt-2 text-sm text-gray-600">
          <span>В сила от: {new Date(terms.effective_date).toLocaleDateString('bg-BG')}</span>
          <span>Последна промяна: {new Date(terms.last_updated).toLocaleDateString('bg-BG')}</span>
          <Badge variant="outline">Версия {terms.version}</Badge>
        </div>
      </div>

      {hasFullText ? (
        // Render full text with proper formatting
        <div className="prose prose-sm max-w-none">
          {terms.full_text.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-gray-700 leading-relaxed whitespace-pre-line mb-4">
              {paragraph}
            </p>
          ))}
        </div>
      ) : (
        // Render sectioned content (old format)
        <div className="space-y-6">
          {Object.entries(terms.content).map(([key, section]) => (
            <div key={key} className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                {section.title}
              </h3>
              <p className="text-gray-700 leading-relaxed pl-6">{section.text}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900">{t('importantNotice') || 'Важно съобщение'}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('importantNoticeText') || 'Използвайки SkillSOS, потвърждавате, че сте прочели, разбрали и се съгласявате с тези Общи условия.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (inModal) {
    return (
      <ScrollArea className="h-96 w-full">
        <div className="p-4">
          {content}
        </div>
      </ScrollArea>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <ScrollArea className="h-96">
          {content}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Privacy Policy Component
export const PrivacyPolicy = ({ inModal = false }) => {
  const { t, i18n } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicy();
  }, [i18n.language]); // Refetch when language changes

  const fetchPolicy = async () => {
    try {
      const response = await axios.get(`${API}/legal/privacy?lang=${i18n.language}`);
      setPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch privacy policy:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">{t('loading')}</div>;
  }

  if (!policy) {
    return <div className="text-center p-8 text-red-500">{t('failedToLoad')} {t('privacyPolicy')}</div>;
  }

  // Check if full_text exists (new format) or use content sections (old format)
  const hasFullText = policy.full_text && policy.full_text.length > 0;

  const content = (
    <div className="space-y-6">
      <div className="text-center border-b pb-4">
        <h1 className="text-2xl font-bold text-gray-900">{policy.title}</h1>
        <div className="flex justify-center gap-4 mt-2 text-sm text-gray-600">
          <span>В сила от: {new Date(policy.effective_date).toLocaleDateString('bg-BG')}</span>
          <span>Последна промяна: {new Date(policy.last_updated).toLocaleDateString('bg-BG')}</span>
          <Badge variant="outline" className="bg-green-50 border-green-200 text-green-800">
            GDPR
          </Badge>
        </div>
      </div>

      {hasFullText ? (
        // Render full text with proper formatting
        <div className="prose prose-sm max-w-none">
          {policy.full_text.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-gray-700 leading-relaxed whitespace-pre-line mb-4">
              {paragraph}
            </p>
          ))}
        </div>
      ) : (
        // Render sectioned content (old format)
        <div className="space-y-6">
          {Object.entries(policy.content).map(([key, section]) => (
            <div key={key} className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-600" />
                {section.title}
              </h3>
              <p className="text-gray-700 leading-relaxed pl-6">{section.text}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-green-900">{t('yourRights') || 'Вашите права'}</h4>
            <p className="text-sm text-green-700 mt-1">
              {t('yourRightsText') || 'Под GDPR имате пълни права върху вашите лични данни. Свържете се с нас на admin@skillsos.bg за да упражните тези права.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (inModal) {
    return (
      <ScrollArea className="h-96 w-full">
        <div className="p-4">
          {content}
        </div>
      </ScrollArea>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <ScrollArea className="h-96">
          {content}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Legal Consent Component for Registration - Two separate checkboxes for Terms and Privacy
export const LegalConsent = ({ consent, onConsentChange, required = true, compact = false }) => {
  const { t } = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(consent?.terms_accepted || false);
  const [privacyAccepted, setPrivacyAccepted] = useState(consent?.privacy_accepted || false);
  const [marketingConsent, setMarketingConsent] = useState(consent?.marketing_consent || false);

  useEffect(() => {
    onConsentChange({
      terms_accepted: termsAccepted,
      privacy_accepted: privacyAccepted,
      marketing_consent: marketingConsent,
      all_required_accepted: termsAccepted && privacyAccepted
    });
  }, [termsAccepted, privacyAccepted, marketingConsent, onConsentChange]);

  return (
    <div className={`space-y-3 ${compact ? '' : 'border-t border-gray-200 pt-4'}`}>
      {/* Terms and Conditions - Separate checkbox */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="terms-checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          data-testid="terms-checkbox"
          title=""
        />
        <label htmlFor="terms-checkbox" className="text-sm text-gray-700 flex-1">
          {t('agreeToTerms') || 'Съгласявам се с'}{' '}
          <Dialog>
            <DialogTrigger asChild>
              <button type="button" className="text-purple-600 hover:underline font-medium">
                {t('termsAndConditions') || 'Общи условия'}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>{t('termsAndConditions') || 'Общи условия'}</DialogTitle>
              </DialogHeader>
              <TermsAndConditions inModal={true} />
            </DialogContent>
          </Dialog>
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>

      {/* Privacy Policy - Separate checkbox */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="privacy-checkbox"
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          data-testid="privacy-checkbox"
          title=""
        />
        <label htmlFor="privacy-checkbox" className="text-sm text-gray-700 flex-1">
          {t('agreeToPrivacy') || 'Съгласявам се с'}{' '}
          <Dialog>
            <DialogTrigger asChild>
              <button type="button" className="text-purple-600 hover:underline font-medium">
                {t('privacyPolicy') || 'Политика за поверителност'}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>{t('privacyPolicy') || 'Политика за поверителност'}</DialogTitle>
              </DialogHeader>
              <PrivacyPolicy inModal={true} />
            </DialogContent>
          </Dialog>
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>

      {/* Marketing Consent (Optional) */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="marketing-checkbox"
          checked={marketingConsent}
          onChange={(e) => setMarketingConsent(e.target.checked)}
          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          data-testid="marketing-checkbox"
          title=""
        />
        <label htmlFor="marketing-checkbox" className="text-sm text-gray-600 flex-1">
          {t('consentMarketing') || 'Съгласен/на съм да получавам маркетингови съобщения и промоционални оферти по имейл'}
          <span className="text-gray-400 ml-1">({t('optional') || 'По избор'})</span>
        </label>
      </div>
    </div>
  );
};

// Legal Documents Page
export const LegalDocumentsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Legal Documents</h1>
          <p className="text-gray-600">Our terms, policies, and your rights</p>
        </div>

        <Tabs defaultValue="terms" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="terms" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Terms & Conditions
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Privacy Policy
            </TabsTrigger>
          </TabsList>

          <TabsContent value="terms">
            <TermsAndConditions />
          </TabsContent>

          <TabsContent value="privacy">
            <PrivacyPolicy />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Quick Legal Links Component
export const LegalLinks = ({ className = "" }) => {
  const { t } = useTranslation();
  
  return (
    <div 
      className={`flex items-center gap-4 text-sm text-gray-500 ${className}`}
      style={{ 
        position: 'relative', 
        zIndex: 9999,
        marginRight: '120px' // Add space to avoid overlap with Emergent popup
      }}
    >
      <Dialog>
        <DialogTrigger asChild>
          <button className="hover:text-gray-700 transition-colors">
            {t('termsConditions')}
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('termsConditions')}</DialogTitle>
          </DialogHeader>
          <TermsAndConditions inModal={true} />
        </DialogContent>
      </Dialog>

      <span className="text-gray-300">•</span>

      <Dialog>
        <DialogTrigger asChild>
          <button className="hover:text-gray-700 transition-colors">
            {t('privacyPolicy')}
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('privacyPolicy')}</DialogTitle>
          </DialogHeader>
          <PrivacyPolicy inModal={true} />
        </DialogContent>
      </Dialog>

      <span className="text-gray-300">•</span>
      
      <a 
        href="mailto:admin@skillsos.bg" 
        className="hover:text-gray-700 transition-colors contact-link"
        onClick={(e) => {
          // Mac-specific handling with enhanced logging
          console.log('Contact link clicked - Platform:', navigator.platform);
          if (navigator.platform.includes('Mac')) {
            console.log('Mac detected - opening mailto link to admin@skillsos.bg');
          }
        }}
        style={{ 
          zIndex: 10000, 
          position: 'relative',
          padding: '4px 8px', // Add clickable area
          marginLeft: '4px'
        }}
        title={`${t('contact')}: admin@skillsos.bg`}
      >
        {t('contact')}
      </a>
    </div>
  );
};